import { useCallback, useEffect } from "react";
import { toast } from "react-hot-toast";
import api from "../../../services/api";
import {
  ACTIONS,
  normalizeAccess,
  normalizeScreenPermissionRow,
  syncModuleFlagsFromScreens,
  getScreenCatalogForRole,
  ADMIN_RBAC_CATALOG,
} from "../../../rbac/catalog";

// ---------------------------------------------------------------------------
// Hook that owns the screen / data access editing lifecycle.
//
// Storage shape (per role) - matches src/rbac/catalog.js contract:
//   {
//     <moduleKey>: {
//       view|create|edit|delete|approve|export: boolean,     // module-level
//       screens: {
//         <screenKey>: { view, create, edit, delete, approve, export }
//       }
//     }
//   }
// ---------------------------------------------------------------------------
export default function useAccessCrud({
  isSuperAdmin,
  screenAccess,
  setScreenAccess,
  dataAccess,
  setDataAccess,
  selectedRoleForAccess,
  canEditScreenAccess = true,
  canEditDataAccess = true,
  roles = [],
  accessCatalog = ADMIN_RBAC_CATALOG,
}) {
  const catalogForStoredRoleId = (roleId) => {
    const role = roles.find((r) => String(r.id) === String(roleId));
    return getScreenCatalogForRole(role);
  };

  const sanitizeScreenAccessForSubmit = (access) => {
    const source = access && typeof access === "object" ? access : {};
    const cleaned = {};

    for (const [moduleKey, moduleValue] of Object.entries(source)) {
      if (!moduleValue || typeof moduleValue !== "object") continue;
      const screens = {};
      if (moduleValue.screens && typeof moduleValue.screens === "object") {
        for (const [screenKey, screenValue] of Object.entries(moduleValue.screens)) {
          if (!screenValue || typeof screenValue !== "object") continue;
          const screenPerms = {};
          for (const action of ACTIONS) {
            if (screenValue[action] === true) screenPerms[action] = true;
          }
          if (Object.keys(screenPerms).length > 0) {
            screens[screenKey] = screenPerms;
          }
        }
      }
      const moduleObj = { screens };
      const hasView = Object.values(screens).some((scr) => !!scr.view);
      if (hasView || moduleValue.view === true) {
        moduleObj.view = true;
      }
      cleaned[moduleKey] = moduleObj;
    }

    return cleaned;
  };

  const fetchScreenAccess = useCallback(async () => {
    try {
      const response = await api.get("/global-settings/screen-access");
      if (response.data.success) {
        const raw = response.data.data || {};
        // Coerce every per-role payload through the normalizer so legacy rows
        // that still use the old "Module_Screen" flat keys don't corrupt the UI.
        const cleaned = {};
        for (const roleId of Object.keys(raw)) {
          cleaned[roleId] = normalizeAccess(
            raw[roleId] || {},
            catalogForStoredRoleId(roleId)
          );
        }
        setScreenAccess(cleaned);
      }
    } catch (error) {
      console.error("Error fetching screen access:", error);
      const stored = localStorage.getItem("screenAccess");
      if (stored) {
        const parsed = JSON.parse(stored);
        const cleaned = {};
        for (const roleId of Object.keys(parsed)) {
          cleaned[roleId] = normalizeAccess(
            parsed[roleId] || {},
            catalogForStoredRoleId(roleId)
          );
        }
        setScreenAccess(cleaned);
      } else {
        setScreenAccess({});
      }
    }
  }, [setScreenAccess, roles]);

  const fetchDataAccess = useCallback(async () => {
    try {
      const response = await api.get("/global-settings/data-access");
      if (response.data.success) {
        setDataAccess(response.data.data || {});
      }
    } catch (error) {
      console.error("Error fetching data access:", error);
      const stored = localStorage.getItem("dataAccess");
      setDataAccess(stored ? JSON.parse(stored) : {});
    }
  }, [setDataAccess]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    fetchScreenAccess();
    fetchDataAccess();
  }, [isSuperAdmin, fetchScreenAccess, fetchDataAccess]);

  const handleSelectAllModule = useCallback(
    (moduleKey, roleId, select) => {
      if (!canEditScreenAccess) {
        toast.error("You do not have permission to edit screen access.");
        return;
      }
      const moduleDef = accessCatalog.find((m) => m.key === moduleKey);
      if (!moduleDef) return;

      setScreenAccess((prev) => {
        const roleAcc = prev[roleId] ? { ...prev[roleId] } : {};
        const moduleAcc = roleAcc[moduleKey]
          ? { ...roleAcc[moduleKey], screens: { ...(roleAcc[moduleKey].screens || {}) } }
          : { screens: {} };

        for (const action of ACTIONS) moduleAcc[action] = select;

        moduleDef.screens.forEach((screen) => {
          const screenAcc = {};
          for (const action of ACTIONS) {
            // Only flip actions that exist for this screen; keep others false.
            screenAcc[action] = select && screen.actions.includes(action);
          }
          moduleAcc.screens[screen.key] = normalizeScreenPermissionRow(
            screenAcc,
            screen.actions
          );
        });

        syncModuleFlagsFromScreens(moduleAcc, moduleDef);

        roleAcc[moduleKey] = moduleAcc;
        return { ...prev, [roleId]: roleAcc };
      });
    },
    [canEditScreenAccess, setScreenAccess, accessCatalog]
  );

  const handleScreenPermissionChange = useCallback(
    (roleId, moduleKey, screenKey, action, value) => {
      if (!canEditScreenAccess) {
        toast.error("You do not have permission to edit screen access.");
        return;
      }
      setScreenAccess((prev) => {
        const moduleDef = accessCatalog.find((m) => m.key === moduleKey);
        const screenDef = moduleDef?.screens?.find((s) => s.key === screenKey);
        if (!moduleDef || !screenDef) return prev;

        const roleAcc = prev[roleId] ? { ...prev[roleId] } : {};
        const moduleAcc = roleAcc[moduleKey]
          ? { ...roleAcc[moduleKey], screens: { ...(roleAcc[moduleKey].screens || {}) } }
          : { screens: {} };
        const screenAcc = moduleAcc.screens[screenKey]
          ? { ...moduleAcc.screens[screenKey] }
          : {};

        screenAcc[action] = !!value;
        moduleAcc.screens[screenKey] = normalizeScreenPermissionRow(
          screenAcc,
          screenDef.actions,
          action === "view" && !value ? { preferViewOff: true } : {}
        );

        syncModuleFlagsFromScreens(moduleAcc, moduleDef);

        roleAcc[moduleKey] = moduleAcc;
        return { ...prev, [roleId]: roleAcc };
      });
    },
    [canEditScreenAccess, setScreenAccess, accessCatalog]
  );

  const handleScreenAccessSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!canEditScreenAccess) {
        toast.error("You do not have permission to save screen access.");
        return;
      }
      try {
        const payload = {
          roleId: selectedRoleForAccess,
          screenAccess: sanitizeScreenAccessForSubmit(
            screenAccess[selectedRoleForAccess] || {}
          ),
        };
        try {
          await api.post("/global-settings/screen-access", payload);
        } catch (apiError) {
          const stored = localStorage.getItem("screenAccess");
          const currentData = stored ? JSON.parse(stored) : {};
          currentData[selectedRoleForAccess] = sanitizeScreenAccessForSubmit(
            screenAccess[selectedRoleForAccess] || {}
          );
          localStorage.setItem("screenAccess", JSON.stringify(currentData));
        }
        toast.success("Screen access saved successfully!");
        await fetchScreenAccess();
      } catch (error) {
        console.error("Error saving screen access:", error);
        toast.error("Failed to save screen access. Please try again.");
      }
    },
    [canEditScreenAccess, selectedRoleForAccess, screenAccess, fetchScreenAccess]
  );

  const handleDataAccessChange = useCallback(
    (roleId, field, value) => {
      if (!canEditDataAccess) {
        toast.error("You do not have permission to edit data access.");
        return;
      }
      setDataAccess((prev) => ({
        ...prev,
        [roleId]: { ...(prev[roleId] || {}), [field]: value },
      }));
    },
    [canEditDataAccess, setDataAccess]
  );

  const handleDataAccessItemToggle = useCallback(
    (roleId, itemId, checked) => {
      if (!canEditDataAccess) {
        toast.error("You do not have permission to edit data access.");
        return;
      }
      setDataAccess((prev) => {
        const current = prev[roleId] || {};
        const selectedItems = current.selectedItems || [];
        return {
          ...prev,
          [roleId]: {
            ...current,
            selectedItems: checked
              ? [...selectedItems, itemId]
              : selectedItems.filter((id) => id !== itemId),
          },
        };
      });
    },
    [canEditDataAccess, setDataAccess]
  );

  const handleDataAccessSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!canEditDataAccess) {
        toast.error("You do not have permission to save data access.");
        return;
      }
      try {
        const payload = {
          roleId: selectedRoleForAccess,
          dataAccess: dataAccess[selectedRoleForAccess],
        };
        try {
          await api.post("/global-settings/data-access", payload);
        } catch (apiError) {
          const stored = localStorage.getItem("dataAccess");
          const currentData = stored ? JSON.parse(stored) : {};
          currentData[selectedRoleForAccess] =
            dataAccess[selectedRoleForAccess];
          localStorage.setItem("dataAccess", JSON.stringify(currentData));
        }
        toast.success("Data access saved successfully!");
        await fetchDataAccess();
      } catch (error) {
        console.error("Error saving data access:", error);
        toast.error("Failed to save data access. Please try again.");
      }
    },
    [canEditDataAccess, selectedRoleForAccess, dataAccess, fetchDataAccess]
  );

  return {
    fetchScreenAccess,
    fetchDataAccess,
    handleSelectAllModule,
    handleScreenPermissionChange,
    handleScreenAccessSubmit,
    handleDataAccessChange,
    handleDataAccessItemToggle,
    handleDataAccessSubmit,
  };
}
